import ChevronRightIcon from '@heroicons/react/outline/ChevronRightIcon';
import cx from 'classnames';
import React from 'react';
import { Helmet } from 'react-helmet';
import { AnchorLink } from '../shared/anchor-link';

export interface BreadcrumbsProps {
  className?: string;
  steps: { name: string; url?: string }[];
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  className,
  steps,
}) => {
  // Generate structured data for breadcrumbs (JSON-LD) for SEO
  const structuredData = React.useMemo(() => {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": steps.map((step, index) => {
        const item = {
          "@type": "ListItem",
          "position": index + 1,
          "name": step.name,
        };

        // Add URL if available and valid
        if (step.url) {
          const fullUrl = step.url.startsWith('http') 
            ? step.url 
            : `${baseUrl}${step.url.startsWith('/') ? step.url : `/${step.url}`}`;
          
          item["item"] = {
            "@type": "WebPage",
            "@id": fullUrl
          };
        }

        return item;
      })
    };
  }, [steps]);

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      <div
        className={cx(
          className,
          'max-w-8xl mx-auto py-4 lg:py-8 lg:pb-0',
          'text-xs font-normal text-slate-400 flex items-center flex-wrap gap-4 overflow-x-hidden'
        )}
      >
      {steps.map((elem, i) => (
        <React.Fragment key={i}>
          {i > 0 && <ChevronRightIcon className="h-3 text-slate-500" />}
          {elem.url ? (
            <AnchorLink className="hover:text-green" to={elem.url}>
              {elem.name}
            </AnchorLink>
          ) : (
            elem.name
          )}
        </React.Fragment>
      ))}
      </div>
    </>
  );
};
